import axios from "axios";
import type { InternalAxiosRequestConfig, AxiosResponse } from "axios";
import { ElMessage } from "element-plus";
import router from "@/router";
import type { ApiResponse } from "@/api/user";
import { errorCode } from "@/utils/errorCode";
import { formatDateTime, parseDateTime } from "./dateFormat";

// 创建 axios 实例
const service = axios.create({
  // 从环境变量获取基础URL
  baseURL: import.meta.env.VITE_APP_BASE_API,
  // 超时时间
  timeout: 10000,
  // 请求头
  headers: {
    "Content-Type": "application/json;charset=utf-8",
  },
});

// 在创建 axios 实例后添加
console.log("API 基础地址:", import.meta.env.VITE_APP_BASE_API);

// 请求拦截器
// 请求拦截器修改（重点关注 GET 处理）
service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("token");

    // 添加调试日志
    console.log("Current URL:", config.url);
    console.log("Token from localStorage:", token);

    // 修改token设置逻辑
    if (token && !config.url?.includes("/admin/admin/login")) {
      config.headers.set("token", token);
      // 添加调试日志
      console.log("Request Headers after setting token:", config.headers);
    }

    // 如果是文件上传，确保设置正确的 Content-Type
    // 修改 FormData 处理逻辑
    if (config.data instanceof FormData) {
      // 删除手动设置 Content-Type，让浏览器自动处理
      delete config.headers["Content-Type"];

      // 确保 token 被添加
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.set("token", token); // 使用 set 方法确保覆盖
      }
    }

    // 特殊处理 GET + Body 请求
    if (config.method?.toLowerCase() === "get" && config.data) {
      // 1. 强制设置 Content-Type
      config.headers.set("Content-Type", "application/json;charset=utf-8");

      // 2. 序列化数据（处理日期格式后）
      if (config.data) {
        const processData = (data: any) => {
          for (const key in data) {
            if (data[key] instanceof Date) {
              data[key] = formatDateTime(data[key]);
            } else if (typeof data[key] === "object" && data[key] !== null) {
              processData(data[key]);
            }
          }
        };
        processData(config.data);
        config.data = JSON.stringify(config.data); // 关键序列化
      }

      // 3. 添加调试日志
      console.log("[GET with Body]", {
        url: config.url,
        data: config.data,
      });
    }

    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    // 使用泛型类型
    console.log(
      `[${response.config.method?.toUpperCase()}] ${response.config.url}`,
      {
        params: response.config.params,
        data: response.config.data,
      }
    );
    const res = response.data as any;
    if (res.code === 1) {
      // 处理响应数据中的日期字符串
      const processData = (data: any) => {
        for (const key in data) {
          if (
            typeof data[key] === "string" &&
            /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(data[key])
          ) {
            data[key] = parseDateTime(data[key]);
          } else if (typeof data[key] === "object" && data[key] !== null) {
            processData(data[key]);
          }
        }
      };

      if (res) {
        processData(res);
      }
      return Promise.resolve(res);
    } else {
      ElMessage.error(res.msg || "请求失败");
      return Promise.reject(new Error(res.msg || "请求失败"));
    }
  },
  (error) => {
    if (error.response) {
      console.error(
        `[${error.config.method?.toUpperCase()}] ${error.config.url} 请求失败:`,
        {
          status: error.response.status,
          data: error.response.data,
        }
      );
    }
    if (!error.response) {
      ElMessage.error("网络错误，请检查您的网络连接");
      return Promise.reject(error);
    }

    const status = error.response.status;
    if (status === 401) {
      ElMessage.error("登录已过期，请重新登录");
      localStorage.clear();
      router.push("/login");
    } else if (status === 500) {
      ElMessage.error("服务器内部错误，请稍后重试");
    } else {
      ElMessage.error(errorCode[status] || errorCode.default);
    }

    return Promise.reject(error);
  }
);

// 导出实例
export default service;
